/**
 * <step-gamer-carousel> — horizontal scroll-snap carousel
 * Attributes: data-autoplay (ms), data-loop
 */
if (!customElements.get('step-gamer-carousel')) {
  class StepGamerCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.dots = null;
      this.index = 0;
      this.timer = null;
      this.onResize = this.onResize.bind(this);
      this.onSwipeStart = this.onSwipeStart.bind(this);
      this.onSwipeEnd = this.onSwipeEnd.bind(this);
      this.onSwipeCancel = this.onSwipeCancel.bind(this);
      this.swipeStartX = 0;
      this.swipeStartY = 0;
      this.swipePointerId = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-sg-carousel-track]');
      this.prevBtn = this.querySelector('[data-sg-carousel-prev]');
      this.nextBtn = this.querySelector('[data-sg-carousel-next]');
      this.dots = this.querySelector('[data-sg-carousel-dots]');
      if (!this.track) return;

      this.slides = Array.from(this.track.children);
      this.swapSlides = this.hasAttribute('data-sg-hero-carousel');
      if (this.swapSlides) {
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === 0));
        this.syncHeroHeight();
        const firstImage = this.slides[0]?.querySelector('img');
        if (firstImage && !firstImage.complete) {
          firstImage.addEventListener('load', () => this.syncHeroHeight(), { once: true });
        }
      }
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1));
      if (!this.swapSlides) {
        this.track.addEventListener('scroll', () => this.syncFromScroll(), { passive: true });
      }
      window.addEventListener('resize', this.onResize);
      this.buildDots();
      this.startAutoplay();
      if (this.swapSlides) this.updateUI();
      else this.syncFromScroll();
      this.bindSwipe();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResize);
      this.unbindSwipe();
    }

    bindSwipe() {
      if (!this.swapSlides) return;
      this.addEventListener('pointerdown', this.onSwipeStart);
      this.addEventListener('pointerup', this.onSwipeEnd);
      this.addEventListener('pointercancel', this.onSwipeCancel);
    }

    unbindSwipe() {
      this.removeEventListener('pointerdown', this.onSwipeStart);
      this.removeEventListener('pointerup', this.onSwipeEnd);
      this.removeEventListener('pointercancel', this.onSwipeCancel);
    }

    onSwipeStart(event) {
      if (event.target.closest('a, button, [data-sg-hero-pagination]')) return;
      this.swipePointerId = event.pointerId;
      this.swipeStartX = event.clientX;
      this.swipeStartY = event.clientY;
    }

    onSwipeEnd(event) {
      if (this.swipePointerId == null || event.pointerId !== this.swipePointerId) return;
      this.swipePointerId = null;
      const dx = event.clientX - this.swipeStartX;
      const dy = event.clientY - this.swipeStartY;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      this.go(dx < 0 ? this.index + 1 : this.index - 1);
    }

    onSwipeCancel() {
      this.swipePointerId = null;
    }

    onResize() {
      if (this.swapSlides) {
        this.syncHeroHeight();
        return;
      }
      this.go(this.index, false);
    }

    syncHeroHeight() {
      const active = this.slides.find((slide) => slide.classList.contains('is-active')) || this.slides[0];
      if (!active || !this.track) return;
      this.track.style.height = `${active.offsetHeight}px`;
    }

    buildDots() {
      if (!this.dots) return;
      this.dots.innerHTML = '';
      this.slides.forEach((_, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'step-gamer-carousel__dot';
        btn.setAttribute('aria-label', 'Slide ' + (i + 1));
        btn.addEventListener('click', () => this.go(i));
        this.dots.appendChild(btn);
      });
    }

    startAutoplay() {
      const ms = parseInt(this.getAttribute('data-autoplay') || '0', 10);
      if (!ms || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.stopAutoplay();
      this.timer = setInterval(() => this.go(this.index + 1), ms);
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    go(i, smooth = true) {
      if (!this.slides.length) return;
      const loop = this.hasAttribute('data-loop');
      let next = i;
      if (loop) {
        next = ((i % this.slides.length) + this.slides.length) % this.slides.length;
      } else {
        next = Math.max(0, Math.min(i, this.slides.length - 1));
      }
      const slide = this.slides[next];
      if (this.swapSlides) {
        if (next === this.index && slide?.classList.contains('is-active')) return;
        const goingNext = next > this.index || (this.index === this.slides.length - 1 && next === 0);
        this.dataset.sgDir = goingNext ? 'next' : 'prev';
        this.index = next;
        this.slides.forEach((item, n) => item.classList.toggle('is-active', n === next));
        requestAnimationFrame(() => this.syncHeroHeight());
      } else {
        this.index = next;
        if (slide) {
          this.track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
        }
      }
      this.updateUI();
      this.startAutoplay();
    }

    syncFromScroll() {
      if (this.swapSlides || !this.slides.length) return;
      const left = this.track.scrollLeft;
      let closest = 0;
      let min = Infinity;
      this.slides.forEach((slide, i) => {
        const d = Math.abs(slide.offsetLeft - left);
        if (d < min) {
          min = d;
          closest = i;
        }
      });
      this.index = closest;
      this.updateUI();
    }

    updateUI() {
      if (this.dots) {
        Array.from(this.dots.children).forEach((dot, i) => {
          dot.classList.toggle('is-active', i === this.index);
          dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
        });
      }
      if (this.prevBtn && !this.hasAttribute('data-loop')) {
        this.prevBtn.disabled = this.index === 0;
      }
      if (this.nextBtn && !this.hasAttribute('data-loop')) {
        this.nextBtn.disabled = this.index === this.slides.length - 1;
      }
      this.dispatchEvent(
        new CustomEvent('sg:carousel:change', {
          detail: { index: this.index },
          bubbles: true,
        })
      );
    }
  }

  customElements.define('step-gamer-carousel', StepGamerCarousel);
}
